import React, { useEffect, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

interface SnackbarLayoutProps {
  children: React.ReactNode;
}

const TOP_MARGIN = 8;
const TOP_OFFSET_POSITION = 46;
const MARGIN_HEIGHT_ADJUSTMENT = 18;
const ANIMATION_DURATION = 500; // ms
const MOBILE_BREAKPOINT = 480;

const calculateTopPosition = (bodyWidth: number) => {
  return bodyWidth > MOBILE_BREAKPOINT ? MOBILE_BREAKPOINT + TOP_OFFSET_POSITION : bodyWidth + TOP_OFFSET_POSITION;
};

const SnackbarLayout = ({ children }: SnackbarLayoutProps) => {
  const theme = useTheme();

  const [isVisible, setIsVisible] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [wrapperHeight, setWrapperHeight] = useState(0);
  const [initialAnimation, setInitialAnimation] = useState(true);

  // 참조
  const containerRef = useRef<HTMLDivElement>(null);

  // 스크롤 이벤트 핸들러
  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    const currentTopPosition = calculateTopPosition(document.body.clientWidth);

    setIsSticky(currentScrollY > currentTopPosition - (theme.gnbHeight.new + TOP_MARGIN));
  };

  // 컨테이너의 크기 변화를 감지하여 wrapper의 높이를 자동으로 조절
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      const containerHeight = entries[0].contentRect.height;
      setWrapperHeight(containerHeight - MARGIN_HEIGHT_ADJUSTMENT);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // 초기 마운트 및 이벤트 리스너 설정
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    handleScroll();

    setTimeout(() => {
      setIsVisible(true);
    }, 0);

    // 애니메이션 완료 후 transition 제거
    setTimeout(() => {
      setInitialAnimation(false);
    }, ANIMATION_DURATION);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Wrapper $height={wrapperHeight} $initialAnimation={initialAnimation}>
        <Container ref={containerRef} $isVisible={isVisible} $isSticky={isSticky} $initialAnimation={initialAnimation}>
          {children}
        </Container>
      </Wrapper>
    </>
  );
};

export default SnackbarLayout;

const Wrapper = styled.div<{ $height: number; $initialAnimation: boolean }>`
  position: relative;
  height: ${({ $height }) => $height}px;
  transition: ${({ $initialAnimation }) => ($initialAnimation ? 'height 0.5s ease-in-out' : 'none')};
`;

const Container = styled.div<{
  $isVisible: boolean;
  $isSticky: boolean;
  $initialAnimation: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: ${({ $isVisible }) => ($isVisible ? -8 : 0)}px;
  width: 100%;
  padding: 0 var(--spacing-Margin-Margin6, 20px);
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: ${({ $initialAnimation, $isVisible }) =>
    $initialAnimation && $isVisible ? 'opacity 0.5s ease-in-out, top 0.5s ease-in-out' : 'none'};
  z-index: 6;

  ${({ $isSticky }) =>
    $isSticky &&
    css`
      position: fixed;
      top: 64px;
      left: 0;
      right: 0;
    `}
`;
